import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Scissors, 
  Calendar, 
  CreditCard, 
  Clock,
  ChevronRight,
  Check,
  ArrowLeft,
  DollarSign,
  Users,
  TrendingUp,
  Camera,
  Star,
  Smartphone,
  Bell,
  Share2,
  ArrowRight,
  CheckCircle2
} from "lucide-react";

const StylistLandingPage = () => {
  const [showDemo, setShowDemo] = useState(false);

  const features = [
    {
      icon: Scissors,
      title: "Service Menu",
      description: "Create your custom service menu with pricing, duration, and descriptions."
    },
    {
      icon: Clock,
      title: "Availability Manager",
      description: "Set your working hours, block out personal time, and manage your schedule."
    },
    {
      icon: CreditCard,
      title: "Payment Options",
      description: "Accept Stripe, PayPal, Cash App, Zelle, or cash - whatever works for you."
    },
    {
      icon: Calendar,
      title: "Smart Booking",
      description: "Let clients book online 24/7. Get automatic reminders and reduce no-shows."
    },
    {
      icon: Camera,
      title: "Portfolio Gallery",
      description: "Showcase your best work and attract new clients with your style gallery."
    },
    {
      icon: Users,
      title: "Client Management",
      description: "Keep track of client preferences, history, and build lasting relationships."
    }
  ];

  const stats = [
    { value: "+40%", label: "More Bookings" },
    { value: "50%", label: "Less No-Shows" },
    { value: "24/7", label: "Online Booking" },
    { value: "30", label: "Days Free" }
  ];

  // How It Works steps
  const howItWorksSteps = [
    {
      step: 1,
      title: "Create Your Profile",
      description: "Sign up free, add your photo, bio, and select your profession (Barber, Stylist, Braider, etc.)",
      icon: Users,
      color: "from-blue-500 to-cyan-500"
    },
    {
      step: 2,
      title: "Add Your Services",
      description: "List your services with prices and duration. Haircut $25 (30 min), Fade $35 (45 min), etc.",
      icon: Scissors,
      color: "from-orange-500 to-amber-500"
    },
    {
      step: 3,
      title: "Set Your Schedule",
      description: "Choose your working days and hours. Block off lunch breaks and personal time.",
      icon: Clock,
      color: "from-purple-500 to-indigo-500"
    },
    {
      step: 4,
      title: "Share Your Booking Link",
      description: "Get your personal booking link. Share on Instagram, Facebook, or text to clients.",
      icon: Share2,
      color: "from-pink-500 to-rose-500"
    },
    {
      step: 5,
      title: "Get Booked & Paid",
      description: "Clients book online. You get notified. Accept payments your way. Done!",
      icon: DollarSign,
      color: "from-green-500 to-emerald-500"
    }
  ];

  // Diverse stylist images - Black female, Latino, Black male barbers
  const stylistImages = [
    {
      src: "https://images.pexels.com/photos/3993467/pexels-photo-3993467.jpeg?auto=compress&w=600",
      alt: "Black female hairstylist braiding hair",
      label: "Hair Stylist"
    },
    {
      src: "https://images.pexels.com/photos/7697673/pexels-photo-7697673.jpeg?auto=compress&w=600",
      alt: "Black male barber giving fade haircut",
      label: "Barber"
    },
    {
      src: "https://images.pexels.com/photos/3998429/pexels-photo-3998429.jpeg?auto=compress&w=600",
      alt: "Latino barber styling client",
      label: "Barber"
    },
    {
      src: "https://images.pexels.com/photos/3992870/pexels-photo-3992870.jpeg?auto=compress&w=600",
      alt: "Female stylist coloring hair",
      label: "Colorist"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-orange-100/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ArrowLeft className="w-5 h-5 text-gray-500" />
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 via-red-500 to-amber-400 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/30">
                <Scissors className="w-5 h-5 text-white" />
              </div>
              <span className="font-display font-bold text-xl text-gray-800">MyHairChair</span>
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-gray-600 hover:text-orange-600">
                  Log In
                </Button>
              </Link>
              <Link to="/register?role=stylist">
                <Button className="bg-gradient-to-r from-orange-500 via-red-500 to-amber-500 hover:from-orange-600 hover:via-red-600 hover:to-amber-600 text-white shadow-lg shadow-orange-500/30">
                  Start 30-Day Free Trial
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white border border-orange-200 px-4 py-2 rounded-full mb-8 shadow-sm">
                <Scissors className="w-4 h-4 text-orange-500" />
                <span className="text-orange-700 text-sm font-medium">For Barbers & Stylists</span>
              </div>
              
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-800 mb-6 leading-tight">
                Your Chair.
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 via-red-500 to-amber-500"> Your Business.</span>
              </h1>
              
              <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                Set your services, manage your schedule, and accept payments your way. 
                Focus on what you do best — we'll handle the rest.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Link to="/register?role=stylist">
                  <Button className="bg-gradient-to-r from-orange-500 via-red-500 to-amber-500 hover:from-orange-600 hover:via-red-600 hover:to-amber-600 text-white text-lg px-8 py-6 rounded-xl shadow-xl shadow-orange-500/30">
                    Create Your Profile
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  className="border-orange-300 text-gray-700 hover:bg-white text-lg px-8 py-6 rounded-xl"
                  onClick={() => setShowDemo(true)}
                >
                  See How It Works
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="font-display text-2xl font-bold text-gray-800">{stat.value}</div>
                    <div className="text-xs text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero Images - More Diverse */}
            <div className="relative">
              <div className="absolute -top-8 -left-8 w-64 h-64 bg-gradient-to-br from-orange-200 to-amber-200 rounded-full blur-3xl opacity-50"></div>
              <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-gradient-to-br from-red-200 to-orange-200 rounded-full blur-3xl opacity-50"></div>
              
              <div className="relative grid grid-cols-2 gap-4">
                {stylistImages.map((img, index) => (
                  <div key={index} className={`${index === 1 ? 'mt-8' : ''}`}>
                    <div className="rounded-2xl overflow-hidden shadow-2xl ring-4 ring-white/50 relative group">
                      <img 
                        src={img.src}
                        alt={img.alt}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                        {img.label}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Floating Card */}
              <div className="absolute -left-4 top-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl p-4 border border-orange-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">$2,450</p>
                    <p className="text-xs text-gray-500">This month</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section - Demo */}
      {showDemo && (
        <section className="py-20 px-6 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative" id="how-it-works">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-full blur-3xl"></div>
          <div className="max-w-5xl mx-auto relative">
            <div className="text-center mb-16">
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-4">
                How It Works
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Get started in 5 simple steps. Be up and running in under 10 minutes.
              </p>
            </div>

            {/* Steps Flow */}
            <div className="space-y-6">
              {howItWorksSteps.map((item, index) => (
                <div key={item.step} className="relative">
                  <div className="flex items-start gap-6 bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    {/* Step Number */}
                    <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                      <item.icon className="w-8 h-8 text-white" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-orange-400 text-sm font-bold">STEP {item.step}</span>
                        <h3 className="font-display text-xl font-bold text-white">
                          {item.title}
                        </h3>
                      </div>
                      <p className="text-gray-300">
                        {item.description}
                      </p>
                    </div>

                    {/* Checkmark */}
                    <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                  </div>
                  
                  {/* Connector Line */}
                  {index < howItWorksSteps.length - 1 && (
                    <div className="absolute left-8 top-full w-0.5 h-6 bg-gradient-to-b from-white/20 to-transparent"></div>
                  )}
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="text-center mt-12">
              <Link to="/register?role=stylist">
                <Button className="bg-gradient-to-r from-orange-500 via-red-500 to-amber-500 text-white text-lg px-10 py-6 rounded-xl shadow-xl">
                  Start Your Free Profile
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <p className="text-gray-500 mt-4 text-sm">No credit card required</p>
            </div>
          </div>
        </section>
      )}

      {/* Always Visible Mini How It Works */}
      {!showDemo && (
        <section className="py-16 px-6 bg-white/50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-2xl font-bold text-gray-800 mb-8">
              Get Started in Minutes
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              {["Create Profile", "Add Services", "Set Hours", "Share Link", "Get Paid"].map((step, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {i + 1}
                  </div>
                  <span className="text-gray-700 font-medium">{step}</span>
                  {i < 4 && <ArrowRight className="w-4 h-4 text-gray-300 mx-2" />}
                </div>
              ))}
            </div>
            <Button 
              variant="outline" 
              className="mt-8 border-orange-300"
              onClick={() => setShowDemo(true)}
            >
              See Detailed Walkthrough
            </Button>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Professional tools built specifically for barbers and stylists.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 border border-orange-100 hover:shadow-xl hover:shadow-orange-100/50 hover:-translate-y-1 transition-all group"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex items-center justify-center mb-6 group-hover:from-orange-500 group-hover:to-amber-500 transition-all">
                  <feature.icon className="w-7 h-7 text-orange-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-display text-xl font-semibold text-gray-800 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-orange-500 via-red-500 to-amber-500 rounded-3xl p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative">
              <h2 className="font-display text-3xl font-bold mb-4 text-center">
                Accept Payments Your Way
              </h2>
              <p className="text-center text-orange-100 mb-8">
                Choose which payment methods work best for your business
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                {["Stripe", "PayPal", "Cash App", "Zelle"].map((method) => (
                  <div key={method} className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                    <CreditCard className="w-8 h-8 mx-auto mb-2" />
                    <span className="text-sm font-medium">{method}</span>
                  </div>
                ))}
              </div>
              <div className="text-center">
                <Link to="/register?role=stylist">
                  <Button className="bg-white text-orange-600 hover:bg-orange-50 text-lg px-10 py-6 rounded-xl shadow-xl">
                    Start 30-Day Free Trial
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section with Diverse Stylists */}
      <section className="py-20 px-6 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-3xl font-bold text-center text-gray-800 mb-12">
            Trusted by Professionals Like You
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Keisha Williams",
                role: "Braider & Natural Hair Stylist",
                location: "Atlanta, GA",
                image: "https://images.pexels.com/photos/3993467/pexels-photo-3993467.jpeg?auto=compress&w=150&h=150&fit=crop",
                quote: "MyHairChair helped me organize my bookings and now I'm fully booked every week!"
              },
              {
                name: "Carlos Martinez",
                role: "Master Barber",
                location: "Houston, TX",
                image: "https://images.pexels.com/photos/3998429/pexels-photo-3998429.jpeg?auto=compress&w=150&h=150&fit=crop",
                quote: "The booking link is perfect for my Instagram. Clients book directly without DMs."
              },
              {
                name: "Marcus Johnson",
                role: "Fade Specialist",
                location: "Chicago, IL",
                image: "https://images.pexels.com/photos/7697673/pexels-photo-7697673.jpeg?auto=compress&w=150&h=150&fit=crop",
                quote: "No more no-shows! The reminders keep my chair full and my money right."
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 border border-orange-100 shadow-sm">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-gray-800">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                    <p className="text-xs text-gray-400">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-orange-100 bg-white/50">
        <div className="max-w-6xl mx-auto text-center text-sm text-gray-500">
          © 2025 MyHairChair. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default StylistLandingPage;
